import Flatten from "@flatten-js/core";

import { defaultCurveConfig, getControlPoint, getXOffsetFactor, getYOffsetFactor } from "./curve";
import { getGeometricConnector } from "./geometricConnector";
import { getGeometricScene } from "./geometricScene";
import type {
  Anchor,
  BoxedConnector,
  ConnectorCurveDescriptor,
  ConnectorEdges,
  CurvableConnector,
  CurveConfig,
  GeometricConnector,
} from "./types";

const { point } = Flatten;

const getXTensionFactor = (from: Anchor, to: Anchor, defValue: number): number => {
  let tensionFactor = defValue;
  if (from.point.x < to.point.x) {
    if (from.edge === "RIGHT") {
      tensionFactor -= 0.05;
    } else if (from.edge === "LEFT") {
      tensionFactor += 0.05;
    }
    if (to.edge === "RIGHT") {
      tensionFactor += 0.05;
    } else if (to.edge === "LEFT") {
      tensionFactor -= 0.05;
    }
  } else {
    if (from.edge === "LEFT") {
      tensionFactor -= 0.05;
    } else if (from.edge === "RIGHT") {
      tensionFactor += 0.05;
    }
    if (to.edge === "LEFT") {
      tensionFactor += 0.05;
    } else if (to.edge === "RIGHT") {
      tensionFactor -= 0.05;
    }
  }
  return tensionFactor;
};

const getYTensionFactor = (from: Anchor, to: Anchor, defValue: number): number => {
  let tensionFactor = defValue;
  if (from.point.y < to.point.y) {
    if (from.edge === "BOTTOM") {
      tensionFactor -= 0.05;
    } else if (from.edge === "TOP") {
      tensionFactor += 0.05;
    }
    if (to.edge === "BOTTOM") {
      tensionFactor += 0.05;
    } else if (to.edge === "TOP") {
      tensionFactor -= 0.05;
    }
  } else {
    if (from.edge === "TOP") {
      tensionFactor -= 0.05;
    } else if (from.edge === "BOTTOM") {
      tensionFactor += 0.05;
    }
    if (to.edge === "TOP") {
      tensionFactor += 0.05;
    } else if (to.edge === "BOTTOM") {
      tensionFactor -= 0.05;
    }
  }
  return tensionFactor;
};

export const getConnectorCurveDescriptor = ({
  connector,
  options: { tension, straight, fromPadding, toPadding, minControlDistance = 0 },
  bidirectional,
}: {
  connector: GeometricConnector;
  options: CurveConfig;
  bidirectional?: boolean;
}): ConnectorCurveDescriptor => {
  const startPadding = bidirectional ? toPadding : fromPadding;
  const startOffset = point(
    connector.from.point.x + startPadding * getXOffsetFactor(connector.from.edge),
    connector.from.point.y + startPadding * getYOffsetFactor(connector.from.edge),
  );

  const startCurve = point(
    connector.from.point.x + (startPadding + straight) * getXOffsetFactor(connector.from.edge),
    connector.from.point.y + (startPadding + straight) * getYOffsetFactor(connector.from.edge),
  );

  const endCurve = point(
    connector.to.point.x + (toPadding + straight) * getXOffsetFactor(connector.to.edge),
    connector.to.point.y + (toPadding + straight) * getYOffsetFactor(connector.to.edge),
  );

  const endOffset = point(
    connector.to.point.x + toPadding * getXOffsetFactor(connector.to.edge),
    connector.to.point.y + toPadding * getYOffsetFactor(connector.to.edge),
  );

  const xTensionFactor = bidirectional ? tension : getXTensionFactor(connector.from, connector.to, tension);
  const yTensionFactor = bidirectional ? tension : getYTensionFactor(connector.from, connector.to, tension);
  const xControlDistance = Math.max(Math.abs(startCurve.x - endCurve.x) * xTensionFactor, minControlDistance);
  const yControlDistance = Math.max(Math.abs(startCurve.y - endCurve.y) * yTensionFactor, minControlDistance);

  const controlPointA = getControlPoint(connector.from.edge, startCurve, xControlDistance, yControlDistance);
  const controlPointB = getControlPoint(connector.to.edge, endCurve, xControlDistance, yControlDistance);

  const m0x = (connector.from.point.x + controlPointA.x) * 0.5;
  const m1x = (controlPointA.x + controlPointB.x) * 0.5;
  const m2x = (controlPointB.x + connector.to.point.x) * 0.5;

  const m0y = (connector.from.point.y + controlPointA.y) * 0.5;
  const m1y = (controlPointA.y + controlPointB.y) * 0.5;
  const m2y = (controlPointB.y + connector.to.point.y) * 0.5;

  const midpoint = point((m0x + 2 * m1x + m2x) * 0.25, (m0y + 2 * m1y + m2y) * 0.25);

  return {
    startOffset,
    startCurve,
    controlPointA,
    controlPointB,
    endCurve,
    endOffset,
    midpoint,
  };
};

export const getCurvableConnector = ({
  connector,
  optimalEdges,
  options = defaultCurveConfig,
}: {
  connector: BoxedConnector;
  optimalEdges: ConnectorEdges;
  options: CurveConfig;
}): CurvableConnector => {
  const { bidirectional } = connector;
  const fromGeometry = getGeometricScene(connector.from.bounds);
  const toGeometry = getGeometricScene(connector.to.bounds);

  const geometricConnector = getGeometricConnector(
    fromGeometry.anchors.find((a) => a.edge === optimalEdges.from) || fromGeometry.anchors[0],
    toGeometry.anchors.find((a) => a.edge === optimalEdges.to) || toGeometry.anchors[0],
  );
  const curveDescriptor = getConnectorCurveDescriptor({
    connector: geometricConnector,
    options: { ...options },
    bidirectional,
  });

  return {
    id: connector.id,
    state: connector.state ?? "neutral",
    analyticsState: connector.analyticsState ?? "inactive",

    from: {
      id: connector.from.id,
      bounds: connector.from.bounds,
      anchor: geometricConnector.from,
    },

    to: {
      id: connector.to.id,
      bounds: connector.to.bounds,
      anchor: geometricConnector.to,
    },
    curveDescriptor,
    bidirectional,
    color: connector.color,
    showTouchables: connector.showTouchables,
    dataAttribute: connector.dataAttribute,
    analyticsLabel: connector.analyticsLabel,
  };
};

export const getCurvableConnectors = ({
  connectors,
  optimalEdges,
  options = defaultCurveConfig,
}: {
  connectors: BoxedConnector[];
  optimalEdges: Record<string, ConnectorEdges>;
  options: CurveConfig;
}) =>
  connectors
    .filter((c) => !!optimalEdges[c.id])
    // Always render active or highlighted last so that they appear 'above' other connectors.
    .sort((c) => (c.state === "active" || c.state === "highlighted" ? 1 : -1))
    .map((connector) => {
      return getCurvableConnector({
        connector,
        optimalEdges: optimalEdges[connector.id],
        options,
      });
    });
